import { DeleteIcon } from "@chakra-ui/icons";
import { Box, Flex, Grid, GridItem } from "@chakra-ui/react";
import { MdOutlineEdit } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaButton, RanaHeading, SIZES } from "@nens/ranalib";
import { useFrontendSetting } from "@nens/ranalib";
import { RanaForm } from "@nens/ranalib";

import { EditCancelSubmitButtons } from "@/components/edit-cancel-submit-buttons";

import { RanaFormMapInput } from "../../../lib/rana-form-map-input/rana-form-map-input";
import { useProjectSettings } from "../hooks/use-project-settings";
import { MAX_CHAR_CODE, MAX_CHAR_NAME } from "../utils/project-consts";

import { ProjectDeleteModal } from "./project-delete-modal";

export function ProjectSettings() {
  const intl = useIntl();

  const {
    initialValues,
    project,
    editMode,
    isMutating,
    canWriteProject,
    canManageProject,
    showDeleteModal,
    setShowDeleteModal,
    cancelEdit,
    startEdit,
    validate,
    submit,
  } = useProjectSettings();

  const { maptilerApiKey, isSuccess } = useFrontendSetting();

  if (!isSuccess) return null;
  return (
    <Box>
      <RanaForm initialValues={initialValues} validate={validate} doSubmit={submit}>
        <Flex justifyContent="space-between" alignItems="center" marginBottom={8}>
          <RanaHeading type="h3">
            <FormattedMessage
              id="projectsettings.general_information"
              defaultMessage="General information"
            />
          </RanaHeading>
          {canWriteProject && (
            <EditCancelSubmitButtons
              isEditing={editMode}
              isMutating={isMutating}
              editButtonProps={{
                variant: "primary",
                title: !canManageProject
                  ? intl.formatMessage({
                      id: "project.change.no_permission",
                      defaultMessage: "You don't have permission to change this projects settings",
                    })
                  : undefined,
                onClick: startEdit,
                isDisabled: !canManageProject,
                leftIcon: <MdOutlineEdit />,
                children: (
                  <FormattedMessage id="button.edit_settings" defaultMessage="Edit settings" />
                ),
              }}
              submitButtonProps={{
                variant: "secondary",
                isDisabled: !canManageProject || isMutating,
                as: RanaForm.SubmitButton,
                children: (
                  <FormattedMessage id="button.save_settings" defaultMessage="Save settings" />
                ),
              }}
              cancelButtonProps={{
                variant: "secondary",
                isDisabled: isMutating,
                onClick: cancelEdit,
              }}
            />
          )}
        </Flex>
        <Grid templateColumns="repeat(2, 1fr)" gap={6} mb={8}>
          <GridItem>
            <RanaForm.Input
              field="name"
              isRequired
              label={intl.formatMessage({
                id: "newproject.name.label",
                defaultMessage: "Project name",
              })}
              maxChar={MAX_CHAR_NAME}
              explanation={intl.formatMessage({
                id: "newproject.name.explanation",
                defaultMessage: "Type in a project name of max 128 characters",
              })}
              isReadOnly={!editMode}
            />
            <RanaForm.Input
              field="code"
              isRequired
              label={intl.formatMessage({
                id: "newproject.code.label",
                defaultMessage: "Project code",
              })}
              explanation={intl.formatMessage({
                id: "newproject.code.explanation",
                defaultMessage: "Type in a project code of max 32 characters",
              })}
              maxChar={MAX_CHAR_CODE}
              isReadOnly={!editMode}
            />
            <RanaForm.Input
              field="id"
              isRequired
              label={intl.formatMessage({
                id: "newproject.id.label",
                defaultMessage: "Project ID",
              })}
              isReadOnly
              onFocus={(e) => e.currentTarget.select()}
            />
            <RanaForm.Textarea
              field="description"
              label={intl.formatMessage({
                id: "newproject.description.label",
                defaultMessage: "Project description",
              })}
              explanation={intl.formatMessage({
                id: "newproject.description.explanation",
                defaultMessage:
                  "Type in a project description, for example the objective of the project.",
              })}
              h={52}
              w="full"
              isReadOnly={!editMode}
            />
          </GridItem>
          <GridItem>
            <RanaForm.GeocoderInput
              field="location"
              maptilerApiKey={maptilerApiKey}
              label={intl.formatMessage({
                id: "newproject.geocoder.label",
                defaultMessage: "Location",
              })}
              explanation={intl.formatMessage({
                id: "newproject.geocoder.explanation",
                defaultMessage: "Type in a location",
              })}
              isReadOnly={!editMode}
            />
            <RanaFormMapInput
              field="location"
              mapId="projectLocationMap"
              maptilerApiKey={maptilerApiKey}
              mt={6}
              h={`calc(100% - ${SIZES[32]})`}
              isReadOnly={!editMode}
            />
          </GridItem>
        </Grid>
        <RanaForm.Errors />
      </RanaForm>
      {canWriteProject && (
        <Box borderTopWidth="base">
          <RanaHeading type="h5" my={6}>
            <FormattedMessage id="projectsettings.danger_zone" defaultMessage="Danger zone" />
          </RanaHeading>
          <RanaButton
            title={
              !canManageProject
                ? intl.formatMessage({
                    id: "project.remove.no_permission",
                    defaultMessage: "You don't have permission to remove this project",
                  })
                : undefined
            }
            isDisabled={!canManageProject}
            variant="danger"
            onClick={() => setShowDeleteModal(true)}
            leftIcon={<DeleteIcon />}
          >
            <FormattedMessage id="button.delete_project" defaultMessage="Delete project" />
          </RanaButton>
        </Box>
      )}

      {showDeleteModal && (
        <ProjectDeleteModal project={project} onClose={() => setShowDeleteModal(false)} />
      )}
    </Box>
  );
}
